import PropTypes from 'prop-types';
import React, { Component } from 'react';
import RhinopayPayment from '../components/RhinopayPayment';
import * as AppConstants from '../constants/AppConstants';
import { StringHelpers } from '../helpers';
import { ValidationService, ValidationShapers } from '../services/ValidationService';
import * as RhinopayService from '../services/RhinopayService';
import { removeSpecialCharacters } from '../helpers/StringHelpers';

class RhinopayPaymentContainer extends Component {
  state = {
    bankRoutingNumber: '',
    bankAccountNumber: '',
    cardNumber: '',
    checkNumber: '',
    creditCardType: AppConstants.RHINOPAY_CREDIT_CARD_TYPE_UNKNOWN,
    cvv: '',
    errors: {},
    expirationDate: '',
    isFormSubmissionInProgress: false,
    isPaymentRequestLoading: true,
    isPaymentMade: false,
    isLinkExpired: false,
    nameOnCard: '',
    nameOnCheck: '',
    organizationName: '',
    patientFullName: '',
    patientId: '',
    paymentMethod: 'credit/debit',
    street: '',
    totalPaymentRequestAmount: 0,
    zipcode: '',
    isAchEnabled: false,
    paymentType: '',
    merchantUUID: '',
    accountType: -1,
    paymentRequestUUID: '',
    payrocSessionToken: null,
    payrocSessionLoading: false,
    payrocSessionError: null,
  };

  creditCardFormRef = React.createRef()
  checkFormRef = React.createRef()

  amountCents = () => Math.round(this.state.totalPaymentRequestAmount * 100)

  componentDidMount() {
    document.title = 'Rhinogram | Rhinopay';
    const paymentRequestString = removeSpecialCharacters(this.props.match.params.paymentRequestCode);
    RhinopayService.getPaymentRequestByCode(paymentRequestString)
      .then((response) => {
        if (!response) {
          this.setState({ isLinkExpired: true, isPaymentRequestLoading: false });
        } else {
          const {
            requestAmount,
            userId,
            isPaid,
            firstName,
            lastName,
            orgName,
            uuid,
            isACHEnabled,
            paymentType,
            merchantUUID,
          } = response.data;

          this.setState({
            isPaymentRequestLoading: false,
            totalPaymentRequestAmount: requestAmount,
            patientId: userId.toString(),
            isPaymentMade: !!isPaid,
            patientFullName: `${firstName} ${lastName}`,
            organizationName: orgName,
            paymentRequestUUID: uuid,
            isAchEnabled: !!isACHEnabled,
            paymentType,
            merchantUUID,
          }, () => {
            if (paymentType === AppConstants.PAYMENT_TYPE_PAYROC && merchantUUID) {
              this.refreshPayrocSession();
            }
          });
        }
      })
      .catch(() => {
        this.setState({ isLinkExpired: true, isPaymentRequestLoading: false });
      });
  }

  refreshPayrocSession = () => {
    const { merchantUUID, paymentType } = this.state;
    if (paymentType !== AppConstants.PAYMENT_TYPE_PAYROC || !merchantUUID) {
      return;
    }
    this.setState({
      payrocSessionToken: null,
      payrocSessionLoading: true,
      payrocSessionError: null,
    });
    RhinopayService.payrocSessionToken(merchantUUID, { scenario: 'payment' })
      .then((res) => {
        this.setState({
          payrocSessionToken: res.data.token,
          payrocSessionLoading: false,
        });
      })
      .catch((err) => {
        console.error(err);
        this.setState({
          payrocSessionLoading: false,
          payrocSessionError: err?.response?.data?.message || err?.message || AppConstants.RHINOPAY_ERROR_MESSAGE,
        });
      });
  };

  handleChange = (name, value) => {
    if (name === 'paymentMethod') {
      this.resetPayment();
      this.setState({ [name]: value }, () => {
        if (this.state.paymentType === AppConstants.PAYMENT_TYPE_PAYROC) {
          this.refreshPayrocSession();
        }
      });
      return;
    }
    this.setState({ [name]: value });
  }

  resetPayment = () => {
    this.setState({
      nameOnCard: '',
      cardNumber: '',
      expirationDate: '',
      cvv: '',
      street: '',
      zipcode: '',
      checkNumber: '',
      nameOnCheck: '',
      bankAccountNumber: '',
      bankRoutingNumber: '',
      errors: {},
      accountType: -1,
    });
  }

  breezePaymentSubmission = () => {
    RhinopayService.breezeAuthentication(this.state.merchantUUID)
      .then((response) => {
        let data =
          `password=${response.data.temporaryPassword}&` +
          'contextType=proxynization&' +
          `accountNumber=${this.state.cardNumber}`;
        if (this.state.paymentMethod === 'check') {
          data =
          `password=${response.data.temporaryPassword}&` +
          'contextType=proxynization&' +
          `accountNumber=${this.state.bankAccountNumber}`;
        }
        return RhinopayService.breezeProxynizationAPI(data);
      })
      .then((response) => {
        if (this.state.paymentMethod === 'credit/debit') {
          return this.creditCardSubmissionForBreeze(response.accountNumber);
        } else {
          return this.checkSubmissionForBreeze(response.accountNumber);
        }
      })
      .then((response) => {
        if (response.data.responseCode !== AppConstants.BREEZE_RESPONSE_CODE || response.responseType === AppConstants.BREEZE_EXCEPTION) {
          this.setState({
            isFormSubmissionInProgress: false,
            errors: { paymentError: AppConstants.RHINOPAY_ERROR_MESSAGE },
          });
        } else {
          this.setState({
            isFormSubmissionInProgress: false,
            isPaymentMade: true,
            errors: { paymentError: '' },
          });
        }
      })
      .catch((error) => {
        console.error(error);
        this.setState({
          isFormSubmissionInProgress: false,
          errors: { paymentError: error?.error?.message || AppConstants.RHINOPAY_ERROR_MESSAGE },
        });
      });
  }

  creditCardSubmissionForBreeze = (accountNumber) => {
    const data = {
      amount: this.amountCents(),
      accountNumber: `*${accountNumber}`,
      patientNumber: this.state.patientId,
      paymentRequestUUID: this.state.paymentRequestUUID,
      holderName: this.state.nameOnCard,
      cardExpiryDate: this.state.expirationDate,
    };
    return RhinopayService.postCreditCardPayment(data);
  }

  checkSubmissionForBreeze = (accountNumber) => {
    const data = {
      amount: this.amountCents(),
      accountNumber: `*${accountNumber}`,
      patientNumber: this.state.patientId,
      paymentRequestUUID: this.state.paymentRequestUUID,
      holderName: this.state.nameOnCheck,
      routingNumber: this.state.bankRoutingNumber,
      accountType: this.accountTypeOptions.find((option) => option.id === this.state.accountType).code,
    };
    return RhinopayService.postCheckPayment(data);
  }

  handlePayrocPaymentResponse = (data) => {
    if (data && data.paymentId) {
      this.setState({
        isFormSubmissionInProgress: false,
        isPaymentMade: true,
        errors: { paymentError: '' },
      });
    } else {
      this.setState({
        isFormSubmissionInProgress: false,
        errors: { paymentError: AppConstants.RHINOPAY_ERROR_MESSAGE },
      });
    }
  }

  postPayrocCreditFromToken = (singleUseToken) => {
    const data = {
      amount: this.amountCents(),
      cardToken: singleUseToken,
      patientNumber: this.state.patientId,
      paymentRequestUUID: this.state.paymentRequestUUID,
    };
    return RhinopayService.postCreditCardPayment(data);
  }

  postPayrocCheckFromToken = (singleUseToken) => {
    const data = {
      amount: this.amountCents(),
      cardToken: singleUseToken,
      patientNumber: this.state.patientId,
      paymentRequestUUID: this.state.paymentRequestUUID,
    };
    return RhinopayService.postCheckPayment(data);
  }

  onPayrocCardSubmissionSuccess = ({ token }) => {
    this.setState({ isFormSubmissionInProgress: true, errors: { ...this.state.errors, paymentError: '' } });
    this.postPayrocCreditFromToken(token)
      .then((response) => this.handlePayrocPaymentResponse(response.data))
      .catch((error) => {
        console.error(error);
        const detail = error?.response?.data?.detail
          || error?.response?.data?.message
          || error?.response?.data?.title;
        this.setState({
          isFormSubmissionInProgress: false,
          errors: {
            paymentError: detail || error?.error?.message || AppConstants.RHINOPAY_ERROR_MESSAGE,
          },
        });
      });
  }

  onPayrocAchSubmissionSuccess = ({ token }) => {
    this.setState({ isFormSubmissionInProgress: true, errors: { ...this.state.errors, paymentError: '' } });
    this.postPayrocCheckFromToken(token)
      .then((response) => this.handlePayrocPaymentResponse(response.data))
      .catch((error) => {
        console.error(error);
        const detail = error?.response?.data?.detail
          || error?.response?.data?.message
          || error?.response?.data?.title;
        this.setState({
          isFormSubmissionInProgress: false,
          errors: {
            paymentError: detail || error?.error?.message || AppConstants.RHINOPAY_ERROR_MESSAGE,
          },
        });
      });
  }

  onPayrocHostedFieldError = (err) => {
    console.error(err);
    const message = err?.message || AppConstants.RHINOPAY_ERROR_MESSAGE;
    this.setState({
      errors: { ...this.state.errors, paymentError: message },
    });
  }

  accountTypeOptions = [
    { id: -1, value: 'Select a type', code: '' },
    { id: 1, value: 'Checking', code: AppConstants.BREEZE_CHECKING_ACCOUNT_TYPE },
    { id: 2, value: 'Savings', code: AppConstants.BREEZE_SAVING_ACCOUNT_TYPE },
  ]

  handleSubmitCreditCardForm = () => {
    if (this.state.paymentType === AppConstants.PAYMENT_TYPE_PAYROC) {
      return;
    }
    if (this.state.isFormSubmissionInProgress) return;

    const errors = this.handleValidation();
    this.setState({ errors });
    if (Object.keys(errors).length) return;

    this.setState({ isFormSubmissionInProgress: true });

    if (this.state.paymentType === AppConstants.BREEZE_PAYMENT_TYPE) {
      this.breezePaymentSubmission();
    }
  };

  handleSubmitCheckForm = () => {
    if (this.state.paymentType === AppConstants.PAYMENT_TYPE_PAYROC) {
      return;
    }
    if (this.state.isFormSubmissionInProgress) return;
    const errors = this.handleValidation();
    this.setState({ errors });
    if (Object.keys(errors).length) return;

    this.setState({ isFormSubmissionInProgress: true });

    if (this.state.paymentType === AppConstants.BREEZE_PAYMENT_TYPE) {
      this.breezePaymentSubmission();
    }
  }

  handleValidation = () => {
    let returnVal = {};
    const isPayingWithCard = this.state.paymentMethod === 'credit/debit';
    if (this.state.paymentType === AppConstants.BREEZE_PAYMENT_TYPE) {
      returnVal = isPayingWithCard
        ? ValidationService(ValidationShapers.shapeRhinopayCreditCardPaymentForBreeze(this.state))
        : ValidationService(ValidationShapers.shapeRhinopayCheckPaymentForBreeze(this.state));
    } else if (this.state.paymentType === AppConstants.PAYMENT_TYPE_PAYROC) {
      returnVal = isPayingWithCard
        ? ValidationService(ValidationShapers.shapeRhinopayCreditCardPaymentPayroc())
        : ValidationService(ValidationShapers.shapeRhinopayCheckPaymentPayroc());
    } else {
      returnVal = isPayingWithCard
        ? ValidationService(ValidationShapers.shapeRhinopayCreditCardPayment(this.state))
        : ValidationService(ValidationShapers.shapeRhinopayCheckPayment(this.state));
    }

    if (isPayingWithCard
      && this.state.paymentType === AppConstants.BREEZE_PAYMENT_TYPE
      && !this.validateCreditCard(this.state.cardNumber)) {
      returnVal.cardNumber = 'Not a valid credit card number';
    }

    return returnVal;
  }

  validateCreditCard = (cardNumber) => {
    const cardNumberNoSpaces = StringHelpers.removeAllWhiteSpace(cardNumber);
    if (cardNumberNoSpaces.length < AppConstants.CREDIT_CARD_LENGTH) return false;
    return true;
  };

  handleSetCardType = (cardType) => {
    this.setState({ creditCardType: cardType });
  }

  render() {
    const props = {
      handleSubmitCreditCardForm: this.handleSubmitCreditCardForm,
      handleSubmitCheckForm: this.handleSubmitCheckForm,
      creditCardFormRef: this.creditCardFormRef,
      checkFormRef: this.checkFormRef,
      validateCreditCard: this.validateCreditCard,
      handleChange: this.handleChange,
      handleSetCardType: this.handleSetCardType,
      accountTypeOptions: this.accountTypeOptions,
      onPayrocCardSubmissionSuccess: this.onPayrocCardSubmissionSuccess,
      onPayrocAchSubmissionSuccess: this.onPayrocAchSubmissionSuccess,
      onPayrocHostedFieldError: this.onPayrocHostedFieldError,
      payrocSessionToken: this.state.payrocSessionToken,
      payrocSessionLoading: this.state.payrocSessionLoading,
      payrocSessionError: this.state.payrocSessionError,
      ...this.state,
    };

    return (<RhinopayPayment {...props} />);
  }
}

RhinopayPaymentContainer.propTypes = {
  match: PropTypes.object,
};

export default RhinopayPaymentContainer;
